/**
 * Sidebar Component
 *
 * Main navigation sidebar with scan items for the overlay-based scan engine.
 * Sidebar items have `data-scan-item` — the ScanFocusOverlay handles focus rendering.
 *
 * Note: Scanning features are disabled on caregiver-only pages (hamburger menu destinations)
 *
 * @module Sidebar
 */

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  ArrowRight,
  ArrowLeft,
  Home,
  XCircle,
  Paintbrush,
} from "lucide-react";
import { useInputMethod } from "@/contexts/InputMethodContext";
import { InputMethod } from "@/lib/inputMethods";
import { useTranslation } from "@/hooks/useTranslation";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { isCaregiverRoute } from "@/lib/caregiverRoutes";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface SidebarProps {
  onAction?: (action: string) => void;
  onBack?: () => void;
}

// Menu items - only Back and Home remain in sidebar
const sideMenuItems = [
  { id: "back", labelKey: "back", icon: ArrowRight, action: "back" },
  { id: "home", labelKey: "main", icon: Home, href: "/" },
];

/** Total count of sidebar items for zone registration */
export const SIDEBAR_ITEMS_COUNT = sideMenuItems.length;

export function Sidebar({
  onAction,
  onBack,
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { inputMethod, setInputMethod } = useInputMethod();
  const { t, dir, isRTL } = useTranslation();

  const isHeadTrackingActive = inputMethod === InputMethod.HEAD_TRACKING;
  const isAdmin = useIsAdmin();

  // Disable scanning focus on caregiver-only pages
  const isCaregiverPage = isCaregiverRoute(location.pathname);

  const handleItemClick = (item: typeof sideMenuItems[0]) => {
    if (item.action === "back") {
      if (onBack) {
        onBack();
      } else if (location.pathname !== "/") {
        navigate(-1);
      }
    } else if (item.action && onAction) {
      onAction(item.action);
    } else if (item.href) {
      navigate(item.href);
    }
  };

  const handleDisableHeadTracking = () => {
    setInputMethod(InputMethod.STANDARD_POINTER);
    const settings = getUserSettings();
    saveUserSettings({ ...settings, controlMethod: "standard_pointer" });
    sessionStorage.setItem("controlMethod", "standard_pointer");
    window.dispatchEvent(new CustomEvent("controlMethodChanged", {
      detail: { method: "standard_pointer" }
    }));
  };

  return (
    <aside
      className={cn(
        "flex flex-col min-h-full w-[100px] bg-sidebar relative",
        isRTL ? "border-l border-sidebar-border" : "border-r border-sidebar-border"
      )}
      dir={dir}
      data-scan-zone="sidebar"
      data-design-id="sidebar"
    >
      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto scrollbar-hide">
        {sideMenuItems.map((item, index) => {
          const isActive = item.href && location.pathname === item.href;
          const Icon = item.id === "back" ? (isRTL ? ArrowRight : ArrowLeft) : item.icon;
          const isBackButton = item.id === "back";

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              style={{
                aspectRatio: '1 / 1',
                width: '100%',
                overflow: 'hidden',
              }}
              className={cn(
                "relative flex flex-col items-center justify-center gap-1 px-1 rounded-xl",
                "transition-all duration-200",
                isActive
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "text-sidebar-foreground hover:bg-primary/20 hover:text-primary hover:scale-105 hover:shadow-md",
                isBackButton && !isActive && "hover:bg-accent hover:text-accent-foreground"
              )}
              data-scan-item
            >
              <Icon className="w-6 h-6 flex-shrink-0" />
              <span className="font-medium leading-tight" style={{ fontSize: 'calc(clamp(0.8rem, 1.8vh, 1rem) * var(--font-scale, 1))' }}>
                {t(item.labelKey as any)}
              </span>
            </button>
          );
        })}

        {/* Design Editor Button - admin only */}
        {isAdmin && (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("open-design-panel"))}
          style={{ aspectRatio: '1 / 1', width: '100%', overflow: 'hidden' }}
          className={cn(
            "flex flex-col items-center justify-center gap-1 px-1 rounded-xl",
            "transition-all duration-200 mt-2",
            "text-sidebar-foreground hover:bg-primary/20 hover:text-primary hover:scale-105 hover:shadow-md"
          )}
        >
          <Paintbrush className="w-6 h-6 flex-shrink-0" />
          <span className="font-medium leading-tight" style={{ fontSize: 'clamp(0.8rem, 1.8vh, 1rem)' }}>עיצוב</span>
        </button>
        )}

        {/* Disable Head Tracking Button - hidden on caregiver pages */}
        {isHeadTrackingActive && !isCaregiverPage && (
          <button
            onClick={handleDisableHeadTracking}
            style={{ aspectRatio: '1 / 1', width: '100%', overflow: 'hidden' }}
            className={cn(
              "flex flex-col items-center justify-center gap-1 px-1 rounded-xl",
              "transition-all duration-200 mt-4",
              "bg-destructive/20 text-destructive hover:bg-destructive/30"
            )}
          >
            <XCircle className="w-6 h-6 flex-shrink-0" />
            <span className="font-medium leading-tight" style={{ fontSize: 'clamp(0.8rem, 1.8vh, 1rem)' }}>
              {t("disableTracking")}
            </span>
          </button>
        )}

      </nav>
    </aside>
  );
}
